/**
 * Character Constellation — D3 force-directed graph visualization of character
 * relationships with interactive detail panels.
 */
import { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as d3 from 'd3';

/* ── colour maps ─────────────────────────────────────────────── */

const ARC_COLORS = {
  ascending: '#22C55E',
  transformation: '#22C55E',
  flat: '#F59E0B',
  descending: '#EF4444',
  negative: '#EF4444',
  circular: '#F59E0B',
};

const RELATIONSHIP_COLORS = {
  conflict: '#EF4444',
  alliance: '#22C55E',
  romance: '#FF4D6A',
  mentor: '#7B68EE',
  foil: '#F59E0B',
  mystery: '#A1A1AA',
  family: '#A1A1AA',
};

const RELATIONSHIP_STROKE = {
  conflict: '6,4',
  alliance: null,
  romance: null,
  mentor: '2,4',
  foil: '6,4',
  mystery: null,
  family: null,
};

/* ── helpers ─────────────────────────────────────────────────── */

function scoreColor(score) {
  if (score >= 80) return 'text-emerald-400';
  if (score >= 60) return 'text-amber-400';
  if (score >= 40) return 'text-orange-400';
  return 'text-red-400';
}

function scoreBarColor(score) {
  if (score >= 80) return '#4CAF50';
  if (score >= 60) return '#FFD700';
  if (score >= 40) return '#FF8C42';
  return '#DC143C';
}

function arcColorForCharacter(character, arcsData) {
  if (!arcsData?.length) {
    // Infer from overall_score as fallback
    if (character.overall_score >= 65) return ARC_COLORS.ascending;
    if (character.overall_score >= 40) return ARC_COLORS.flat;
    return ARC_COLORS.descending;
  }
  const arc = arcsData.find(a => a.character === character.name);
  if (!arc) return ARC_COLORS.flat;
  return ARC_COLORS[arc.arc_type] || ARC_COLORS.flat;
}

function nodeRadius(character) {
  const count = character.estimated_line_count || character.estimated_scene_count || 10;
  // Scale: clamp between 20 and 60
  const scale = d3.scaleSqrt().domain([1, 200]).range([20, 60]).clamp(true);
  return scale(count);
}

/* ── ScoreBar (reused from original) ─────────────────────────── */

function ScoreBar({ label, score }) {
  if (score == null) return null;
  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] text-[var(--text-muted)] w-24 shrink-0">{label}</span>
      <div className="flex-1 h-1.5 rounded-full bg-[var(--bg-app)]">
        <motion.div
          className="h-full rounded-full"
          style={{ backgroundColor: scoreBarColor(score) }}
          initial={{ width: 0 }}
          animate={{ width: `${score}%` }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        />
      </div>
      <span className="text-[10px] font-medium text-[var(--text-secondary)] w-6 text-right">{score}</span>
    </div>
  );
}

/* ── Fallback grid ───────────────────────────────────────────── */

function FallbackGrid({ data }) {
  return (
    <div className="space-y-4">
      <Header data={data} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {data.characters.map((char) => (
          <div
            key={char.name}
            className="rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-elevated)] p-4 space-y-2"
          >
            <div className="flex items-center gap-3">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${scoreColor(char.overall_score)}`}
                style={{ backgroundColor: `${scoreBarColor(char.overall_score)}15` }}
              >
                {char.overall_score}
              </div>
              <div>
                <span className="text-sm font-medium text-[var(--text-primary)]">{char.name}</span>
                {char.archetype && (
                  <span className="ml-2 text-[9px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 uppercase tracking-wider">
                    {char.archetype}
                  </span>
                )}
              </div>
            </div>
            <p className="text-[11px] text-[var(--text-muted)]">{char.one_line_description}</p>
          </div>
        ))}
      </div>
      {data.character_balance_assessment && (
        <p className="text-xs text-[var(--text-secondary)] p-3 rounded-lg bg-[var(--bg-elevated)] border border-[var(--border-subtle)]">
          {data.character_balance_assessment}
        </p>
      )}
    </div>
  );
}

/* ── Header ──────────────────────────────────────────────────── */

function Header({ data }) {
  return (
    <>
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-[var(--text-primary)]">Character Analysis</h3>
        <span className={`text-sm font-bold ${scoreColor(data.ensemble_score)}`}>
          Ensemble: {data.ensemble_score}/100
        </span>
      </div>
      <div className="flex gap-4 text-xs">
        {data.protagonist && (
          <span className="text-[var(--text-muted)]">
            Protagonist: <span className="text-amber-400 font-medium">{data.protagonist}</span>
          </span>
        )}
        {data.antagonist && (
          <span className="text-[var(--text-muted)]">
            Antagonist: <span className="text-red-400 font-medium">{data.antagonist}</span>
          </span>
        )}
      </div>
    </>
  );
}

/* ── Detail Panel ────────────────────────────────────────────── */

function DetailPanel({ character, onClose }) {
  if (!character) return null;
  const scores = character.scores || {};

  return (
    <motion.div
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: 'auto', opacity: 1 }}
      exit={{ height: 0, opacity: 0 }}
      transition={{ duration: 0.35, ease: 'easeOut' }}
      className="overflow-hidden"
    >
      <div className="rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-elevated)] p-5 space-y-4 mt-4">
        {/* Close button */}
        <div className="flex items-center justify-between">
          <div>
            <h4
              className="text-lg font-semibold text-[var(--text-primary)]"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {character.name}
            </h4>
            <div className="flex items-center gap-2 mt-1">
              {character.archetype && (
                <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 uppercase tracking-wider">
                  {character.archetype}
                </span>
              )}
              <span className={`text-sm font-bold ${scoreColor(character.overall_score)}`}>
                {character.overall_score}/100
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors text-xs px-2 py-1 rounded border border-[var(--border-subtle)]"
          >
            Close
          </button>
        </div>

        {character.one_line_description && (
          <p className="text-xs text-[var(--text-secondary)]">{character.one_line_description}</p>
        )}

        {/* Score Bars */}
        <div className="space-y-1.5">
          <ScoreBar label="Dimensionality" score={scores.dimensionality} />
          <ScoreBar label="Motivation" score={scores.motivation_clarity} />
          <ScoreBar label="Distinctiveness" score={scores.distinctiveness} />
          <ScoreBar label="Fascination" score={scores.fascination} />
          <ScoreBar label="Dialogue" score={scores.dialogue_fingerprint} />
          <ScoreBar label="Agency" score={scores.agency} />
          <ScoreBar label="Backstory" score={scores.backstory_integration} />
          <ScoreBar label="Chemistry" score={scores.relationship_chemistry} />
          <ScoreBar label="Relatability" score={scores.audience_proxy} />
          <ScoreBar label="Castability" score={scores.castability} />
        </div>

        {/* Assessment */}
        {character.assessment && (
          <p className="text-xs text-[var(--text-secondary)]">{character.assessment}</p>
        )}

        {/* Relationships */}
        {character.relationships?.length > 0 && (
          <div className="space-y-1">
            <span className="text-[10px] font-medium text-[var(--text-secondary)]">Relationships</span>
            <div className="flex flex-wrap gap-1.5">
              {character.relationships.map((rel, i) => (
                <span
                  key={i}
                  className="text-[10px] px-2 py-0.5 rounded-full font-medium"
                  style={{
                    backgroundColor: `${RELATIONSHIP_COLORS[rel.type] || '#666'}20`,
                    color: RELATIONSHIP_COLORS[rel.type] || '#999',
                  }}
                >
                  {rel.character} — {rel.type}
                  {rel.intensity ? ` (${rel.intensity}/10)` : ''}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Notable Lines */}
        {character.notable_lines?.length > 0 && (
          <div className="space-y-1">
            <span className="text-[10px] font-medium text-amber-400">Notable Lines</span>
            {character.notable_lines.map((line, i) => (
              <p key={i} className="text-[11px] text-[var(--text-muted)] italic pl-2 border-l-2 border-amber-500/30">
                &ldquo;{line}&rdquo;
              </p>
            ))}
          </div>
        )}

        {/* Key moments */}
        {(character.most_revealing_moment || character.biggest_missed_opportunity) && (
          <div className="space-y-1.5">
            <span className="text-[10px] font-medium text-[var(--text-secondary)]">Key Moments</span>
            {character.most_revealing_moment && (
              <p className="text-[11px] text-[var(--text-muted)]">
                <span className="text-emerald-400 font-medium">Most revealing:</span>{' '}
                {character.most_revealing_moment}
              </p>
            )}
            {character.biggest_missed_opportunity && (
              <p className="text-[11px] text-[var(--text-muted)]">
                <span className="text-amber-400 font-medium">Missed opportunity:</span>{' '}
                {character.biggest_missed_opportunity}
              </p>
            )}
          </div>
        )}

        {/* New fields */}
        {(character.contradiction || character.first_scene_verdict || character.dinner_test) && (
          <div className="space-y-1.5 border-t border-[var(--border-subtle)] pt-3">
            {character.contradiction && (
              <p className="text-[11px] text-[var(--text-muted)]">
                <span className="text-purple-400 font-medium">Contradiction:</span>{' '}
                {character.contradiction}
              </p>
            )}
            {character.first_scene_verdict && (
              <p className="text-[11px] text-[var(--text-muted)]">
                <span className="text-blue-400 font-medium">First scene verdict:</span>{' '}
                {character.first_scene_verdict}
              </p>
            )}
            {character.dinner_test && (
              <p className="text-[11px] text-[var(--text-muted)]">
                <span className="text-pink-400 font-medium">Dinner test:</span>{' '}
                {character.dinner_test}
              </p>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
}

/* ── Graph component ─────────────────────────────────────────── */

function ConstellationGraph({ data }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const simulationRef = useRef(null);
  const [selectedCharacter, setSelectedCharacter] = useState(null);
  const [dimensions, setDimensions] = useState({ width: 800, height: 400 });
  /* Build graph data */
  const { nodes, links, protagonistName } = useMemo(() => {
    const characters = data.characters || [];

    // Find protagonist — either explicit or character with most lines
    const protName =
      data.protagonist ||
      characters.reduce(
        (best, c) =>
          (c.estimated_line_count || 0) > (best.estimated_line_count || 0) ? c : best,
        characters[0]
      )?.name;

    const charMap = {};
    characters.forEach((c, i) => {
      charMap[c.name] = i;
    });

    const nodeList = characters.map((c) => ({
      id: c.name,
      character: c,
      radius: nodeRadius(c),
      color: arcColorForCharacter(c),
      isProtagonist: c.name === protName,
    }));

    // Build links from relationships (deduplicated)
    const linkSet = new Set();
    const linkList = [];
    characters.forEach((c) => {
      (c.relationships || []).forEach((rel) => {
        const key = [c.name, rel.character].sort().join('||');
        if (linkSet.has(key)) return;
        // Only create link if target character exists in our nodes
        if (charMap[rel.character] == null) return;
        linkSet.add(key);
        linkList.push({
          source: c.name,
          target: rel.character,
          type: rel.type || 'mystery',
          intensity: rel.intensity || 5,
          description: rel.description || '',
        });
      });
    });

    return { nodes: nodeList, links: linkList, protagonistName: protName };
  }, [data]);

  /* Resize observer */
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width } = entry.contentRect;
        if (width > 0) {
          setDimensions({ width, height: 400 });
        }
      }
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [setDimensions]);

  /* D3 Force Simulation */
  useEffect(() => {
    const svg = d3.select(svgRef.current);
    const { width, height } = dimensions;

    // Clear previous contents
    svg.selectAll('*').remove();

    // Defs for gradient + glow
    const defs = svg.append('defs');

    // Radial gradient background
    const bgGrad = defs
      .append('radialGradient')
      .attr('id', 'constellation-bg')
      .attr('cx', '50%')
      .attr('cy', '50%')
      .attr('r', '50%');
    bgGrad.append('stop').attr('offset', '0%').attr('stop-color', '#111114');
    bgGrad.append('stop').attr('offset', '100%').attr('stop-color', '#09090B');

    // Romance glow filter
    const glowFilter = defs.append('filter').attr('id', 'romance-glow');
    glowFilter
      .append('feGaussianBlur')
      .attr('stdDeviation', '3')
      .attr('result', 'coloredBlur');
    const feMerge = glowFilter.append('feMerge');
    feMerge.append('feMergeNode').attr('in', 'coloredBlur');
    feMerge.append('feMergeNode').attr('in', 'SourceGraphic');

    // Background rect
    svg
      .append('rect')
      .attr('width', width)
      .attr('height', height)
      .attr('fill', 'url(#constellation-bg)');

    // Deep-copy nodes/links to avoid D3 mutation issues on re-render
    const simNodes = nodes.map((n) => ({ ...n }));
    const simLinks = links.map((l) => ({ ...l }));

    // Force simulation
    const simulation = d3
      .forceSimulation(simNodes)
      .force(
        'link',
        d3
          .forceLink(simLinks)
          .id((d) => d.id)
          .distance((d) => {
            // Stronger relationships = closer
            const base = 120;
            return base + (10 - d.intensity) * 12;
          })
      )
      .force('charge', d3.forceManyBody().strength(-150))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force(
        'collide',
        d3.forceCollide().radius((d) => d.radius + 10)
      )
      .alphaDecay(0.02)
      .velocityDecay(0.3);

    simulationRef.current = simulation;

    // Container group
    const g = svg.append('g');

    // Links
    const linkGroup = g
      .selectAll('.link')
      .data(simLinks)
      .join('line')
      .attr('class', 'link')
      .attr('stroke', (d) => RELATIONSHIP_COLORS[d.type] || '#A1A1AA')
      .attr('stroke-width', (d) => Math.max(1, d.intensity / 3))
      .attr('stroke-dasharray', (d) => RELATIONSHIP_STROKE[d.type] || null)
      .attr('stroke-opacity', 0.6)
      .attr('filter', (d) => (d.type === 'romance' ? 'url(#romance-glow)' : null));

    // Node groups
    const nodeGroup = g
      .selectAll('.node')
      .data(simNodes)
      .join('g')
      .attr('class', 'node')
      .style('cursor', 'pointer');

    // Circle
    nodeGroup
      .append('circle')
      .attr('r', (d) => d.radius)
      .attr('fill', (d) => d.color)
      .attr('fill-opacity', 0.25)
      .attr('stroke', (d) => (d.isProtagonist ? '#C9A84C' : d.color))
      .attr('stroke-width', (d) => (d.isProtagonist ? 3 : 1.5))
      .attr('stroke-opacity', (d) => (d.isProtagonist ? 1 : 0.5));

    // Inner glow circle
    nodeGroup
      .append('circle')
      .attr('r', (d) => d.radius * 0.6)
      .attr('fill', (d) => d.color)
      .attr('fill-opacity', 0.15);

    // Labels
    nodeGroup
      .append('text')
      .text((d) => d.id)
      .attr('text-anchor', 'middle')
      .attr('dy', (d) => d.radius + 14)
      .attr('fill', '#E4E4E7')
      .attr('font-size', '11px')
      .attr('font-family', "'DM Sans', sans-serif")
      .attr('pointer-events', 'none');

    // Score label inside node
    nodeGroup
      .append('text')
      .text((d) => d.character.overall_score)
      .attr('text-anchor', 'middle')
      .attr('dy', '0.35em')
      .attr('fill', '#E4E4E7')
      .attr('font-size', (d) => `${Math.max(10, d.radius * 0.45)}px`)
      .attr('font-weight', '600')
      .attr('font-family', "'DM Sans', sans-serif")
      .attr('pointer-events', 'none');

    // Tooltip
    const tooltip = d3
      .select(containerRef.current)
      .append('div')
      .style('position', 'absolute')
      .style('pointer-events', 'none')
      .style('background', '#18181B')
      .style('border', '1px solid #27272A')
      .style('border-radius', '8px')
      .style('padding', '8px 12px')
      .style('font-size', '11px')
      .style('font-family', "'DM Sans', sans-serif")
      .style('color', '#E4E4E7')
      .style('opacity', 0)
      .style('z-index', 50)
      .style('transition', 'opacity 0.15s ease');

    // Interaction: hover
    nodeGroup
      .on('mouseenter', function (event, d) {
        const connectedIds = new Set();
        connectedIds.add(d.id);
        simLinks.forEach((l) => {
          const sId = typeof l.source === 'object' ? l.source.id : l.source;
          const tId = typeof l.target === 'object' ? l.target.id : l.target;
          if (sId === d.id) connectedIds.add(tId);
          if (tId === d.id) connectedIds.add(sId);
        });

        nodeGroup
          .transition()
          .duration(200)
          .style('opacity', (n) => (connectedIds.has(n.id) ? 1 : 0.2));

        linkGroup
          .transition()
          .duration(200)
          .attr('stroke-opacity', (l) => {
            const sId = typeof l.source === 'object' ? l.source.id : l.source;
            const tId = typeof l.target === 'object' ? l.target.id : l.target;
            return sId === d.id || tId === d.id ? 0.9 : 0.05;
          })
          .attr('stroke-width', (l) => {
            const sId = typeof l.source === 'object' ? l.source.id : l.source;
            const tId = typeof l.target === 'object' ? l.target.id : l.target;
            return sId === d.id || tId === d.id
              ? Math.max(2, l.intensity / 2)
              : Math.max(1, l.intensity / 3);
          });

        // Tooltip
        tooltip
          .html(
            `<strong>${d.id}</strong>` +
              (d.character.archetype ? `<br/><span style="color:#F59E0B">${d.character.archetype}</span>` : '') +
              `<br/>Score: <span style="color:${d.color};font-weight:600">${d.character.overall_score}/100</span>`
          )
          .style('opacity', 1);
      })
      .on('mousemove', function (event) {
        const containerRect = containerRef.current.getBoundingClientRect();
        tooltip
          .style('left', `${event.clientX - containerRect.left + 12}px`)
          .style('top', `${event.clientY - containerRect.top - 10}px`);
      })
      .on('mouseleave', function () {
        nodeGroup.transition().duration(200).style('opacity', 1);
        linkGroup
          .transition()
          .duration(200)
          .attr('stroke-opacity', 0.6)
          .attr('stroke-width', (d) => Math.max(1, d.intensity / 3));
        tooltip.style('opacity', 0);
      });

    // Interaction: click
    nodeGroup.on('click', function (event, d) {
      event.stopPropagation();
      setSelectedCharacter((prev) =>
        prev?.name === d.character.name ? null : d.character
      );
    });

    // Click on background to deselect
    svg.on('click', () => setSelectedCharacter(null));

    // Drag behavior
    const drag = d3
      .drag()
      .on('start', (event, d) => {
        if (!event.active) simulation.alphaTarget(0.3).restart();
        d.fx = d.x;
        d.fy = d.y;
      })
      .on('drag', (event, d) => {
        d.fx = event.x;
        d.fy = event.y;
      })
      .on('end', (event, d) => {
        if (!event.active) simulation.alphaTarget(0);
        d.fx = null;
        d.fy = null;
      });

    nodeGroup.call(drag);

    // Tick
    simulation.on('tick', () => {
      // Constrain nodes within bounds
      simNodes.forEach((d) => {
        d.x = Math.max(d.radius + 5, Math.min(width - d.radius - 5, d.x));
        d.y = Math.max(d.radius + 5, Math.min(height - d.radius - 20, d.y));
      });

      linkGroup
        .attr('x1', (d) => d.source.x)
        .attr('y1', (d) => d.source.y)
        .attr('x2', (d) => d.target.x)
        .attr('y2', (d) => d.target.y);

      nodeGroup.attr('transform', (d) => `translate(${d.x},${d.y})`);
    });

    // Cleanup
    return () => {
      simulation.stop();
      tooltip.remove();
    };
  }, [nodes, links, dimensions, setSelectedCharacter]);

  /* Handle close */
  const handleCloseDetail = useCallback(() => {
    setSelectedCharacter(null);
  }, [setSelectedCharacter]);

  return (
    <div className="space-y-4">
      <Header data={data} />

      {/* SVG container */}
      <div
        ref={containerRef}
        className="relative rounded-lg overflow-hidden border border-[var(--border-subtle)]"
        style={{ background: '#09090B' }}
      >
        <svg
          ref={svgRef}
          width={dimensions.width}
          height={dimensions.height}
          style={{ display: 'block' }}
        />
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-[var(--text-muted)]">
        {Object.entries(RELATIONSHIP_COLORS).map(([type, color]) => (
          <span key={type} className="flex items-center gap-1.5">
            <span
              className="inline-block w-4 h-0.5 rounded"
              style={{
                backgroundColor: color,
                borderTop: RELATIONSHIP_STROKE[type] ? `2px dashed ${color}` : `2px solid ${color}`,
                height: 0,
              }}
            />
            <span style={{ color }}>{type}</span>
          </span>
        ))}
        <span className="flex items-center gap-1.5 ml-2">
          <span className="inline-block w-3 h-3 rounded-full border-2" style={{ borderColor: '#C9A84C' }} />
          protagonist
        </span>
      </div>

      {/* Detail Panel */}
      <AnimatePresence>
        {selectedCharacter && (
          <DetailPanel
            key={selectedCharacter.name}
            character={selectedCharacter}
            onClose={handleCloseDetail}
          />
        )}
      </AnimatePresence>

      {/* Balance Assessment */}
      {data.character_balance_assessment && (
        <p className="text-xs text-[var(--text-secondary)] p-3 rounded-lg bg-[var(--bg-elevated)] border border-[var(--border-subtle)]">
          {data.character_balance_assessment}
        </p>
      )}
    </div>
  );
}

/* ── Main Component ──────────────────────────────────────────── */

export default function CharacterConstellation({ data }) {
  if (!data?.characters?.length) return null;

  const hasRelationships = data.characters.some(c => c.relationships?.length > 0);
  const isFallback = data.characters.length < 3 || !hasRelationships;

  if (isFallback) {
    return <FallbackGrid data={data} />;
  }

  return <ConstellationGraph data={data} />;
}
